import React, { useCallback, useEffect, useState } from 'react';
import {
  Banknote,
  CheckCircle2,
  Eye,
  FileSignature,
  MessageSquare,
  Search,
  ShieldCheck,
  TimerReset,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import {
  DetailItem,
  DetailModalShell,
  EmptyState,
  ErrorBanner,
  MetricCard,
  PageSkeleton,
  StatusBadge,
  extractError,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  inputClass,
  labelize
} from './hiringUi';

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'negotiation_pending', label: 'Negotiation pending' },
  { value: 'negotiation_countered', label: 'Countered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'expired', label: 'Expired' }
];

const negotiationOptions = [
  { value: 'all', label: 'All negotiations' },
  { value: 'pending_employer_review', label: 'Pending employer review' },
  { value: 'accepted_by_employer', label: 'Accepted by employer' },
  { value: 'rejected_by_employer', label: 'Rejected by employer' },
  { value: 'countered', label: 'Countered' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'superseded', label: 'Superseded' }
];

const signatureOptions = [
  { value: 'all', label: 'All signatures' },
  { value: 'required', label: 'Signature required' },
  { value: 'signed', label: 'Signed' },
  { value: 'missing', label: 'Missing signature' }
];

const sortOptions = [
  { value: 'updated_at', label: 'Recently updated' },
  { value: 'created_at', label: 'Newest' },
  { value: 'expires_at', label: 'Expiry' },
  { value: 'status', label: 'Status' },
  { value: 'salary', label: 'Salary' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'employer', label: 'Employer' }
];

const salaryPeriods = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
  one_time: 'one time'
};

const formatSalary = (offer) => {
  const amount = formatCurrency(offer?.salaryAmount, offer?.salaryCurrency || 'USD');
  const period = salaryPeriods[offer?.salaryPeriod] || labelize(offer?.salaryPeriod).toLowerCase();
  return `${amount}${period ? ` / ${period}` : ''}`;
};

function OfferDetails({ detail }) {
  const offer = detail?.offer;
  const negotiations = detail?.negotiations || [];
  const events = detail?.events || [];
  if (!offer) return null;

  const documentCount = [
    offer.documents?.offerLetterPath,
    offer.documents?.contractDocumentPath,
    offer.documents?.signedOfferLetterPath,
    ...(Array.isArray(offer.documents?.additionalDocuments) ? offer.documents.additionalDocuments.map((item) => item?.path) : [])
  ].filter(Boolean).length;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Offer terms</h3>
          </div>
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <DetailItem label="Status" value={labelize(offer.status)} />
            <DetailItem label="Salary" value={formatSalary(offer)} />
            <DetailItem label="Employment type" value={labelize(offer.employmentType)} />
            <DetailItem label="Start date" value={formatDate(offer.startDate)} />
            <DetailItem label="Expires" value={formatDateTime(offer.expiresAt)} />
            <DetailItem label="Negotiable" value={offer.negotiable ? 'Yes' : 'No'} />
            <DetailItem label="Signature required" value={offer.signatureRequired ? 'Yes' : 'No'} />
            <DetailItem label="Signed at" value={formatDateTime(offer.candidateSignedAt)} />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Negotiations</h3>
          </div>
          {!negotiations.length ? (
            <div className="p-4 text-sm text-slate-500">No negotiation messages have been recorded for this offer.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {negotiations.map((item) => (
                <article key={item.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item.senderName || labelize(item.senderRole)}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatDateTime(item.createdAt)}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-600">{item.message || 'No message supplied.'}</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    <DetailItem label="Proposed salary" value={item.proposedSalaryAmount ? formatCurrency(item.proposedSalaryAmount, item.proposedSalaryCurrency || 'USD') : null} />
                    <DetailItem label="Salary period" value={labelize(item.proposedSalaryPeriod)} />
                    <DetailItem label="Start date" value={formatDate(item.proposedStartDate)} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Event history</h3>
          </div>
          {!events.length ? (
            <div className="p-4 text-sm text-slate-500">No offer events have been recorded.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {events.map((item) => (
                <article key={item.id} className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">{labelize(item.type)}</p>
                    <p className="text-xs text-slate-500">{formatDateTime(item.createdAt)}</p>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{item.actorName || labelize(item.actorRole)}</p>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <aside className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Candidate and role</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Candidate" value={offer.candidate?.name} />
            <DetailItem label="Email" value={offer.candidate?.email} />
            <DetailItem label="Job" value={offer.job?.title} />
            <DetailItem label="Employer" value={offer.employer?.companyName} />
            <DetailItem label="Match score" value={offer.match?.score === null ? null : `${Math.round(offer.match?.score || 0)}%`} />
          </div>
        </section>
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Documents and state</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Documents" value={formatNumber(documentCount)} />
            <DetailItem label="Offer letter" value={offer.documents?.offerLetterPath} />
            <DetailItem label="Contract" value={offer.documents?.contractDocumentPath} />
            <DetailItem label="Signed offer letter" value={offer.documents?.signedOfferLetterPath} />
            <DetailItem label="Negotiations" value={`${formatNumber(offer.negotiationSummary?.total)} total, ${formatNumber(offer.negotiationSummary?.pending)} pending`} />
          </div>
        </section>
      </aside>
    </div>
  );
}

function OffersListPage() {
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    negotiationStatus: 'all',
    signature: 'all',
    sortBy: 'updated_at',
    sortOrder: 'DESC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadOffers = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await adminAPI.getAdminOffers({ ...filters, limit: pagination.limit, offset });
      setOffers(response.offers || []);
      setStats(response.stats || {});
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Offers could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadOffers({ offset: 0 });
  }, [loadOffers]);

  const openDetails = async (offer) => {
    setSelected(offer);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await adminAPI.getAdminOffer(offer.id));
    } catch (detailError) {
      setError(extractError(detailError, 'Offer details could not be loaded.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({ search: '', status: 'all', negotiationStatus: 'all', signature: 'all', sortBy: 'updated_at', sortOrder: 'DESC' });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setFilters((current) => ({ ...current, search: searchInput.trim() }));
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Offer operations</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Platform-wide offer visibility across offer terms, negotiation state, signature requirements, documents, candidate responses, and acceptance outcomes.
            </p>
          </div>
          
        </div>

        <ErrorBanner message={error} />

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard icon={FileSignature} label="Offers" value={formatNumber(stats.total)} detail={`${formatNumber(stats.open)} open`} />
          <MetricCard icon={CheckCircle2} label="Accepted" value={formatNumber(stats.accepted)} detail={`${formatNumber(stats.closed)} closed`} />
          <MetricCard icon={MessageSquare} label="Negotiations" value={formatNumber(stats.negotiations)} detail="Needs commercial follow-up" />
          <MetricCard icon={ShieldCheck} label="Missing signatures" value={formatNumber(stats.signatureMissing)} detail={`${formatNumber(stats.signatureRequired)} require signing`} />
          <MetricCard icon={Banknote} label="Average salary" value={stats.averageSalary === null ? 'No salary' : formatCurrency(stats.averageSalary, 'USD')} detail="USD offer terms" />
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4 sm:p-5">
            <form onSubmit={submitSearch} className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1">
              <label className="relative block min-w-[280px] flex-[1_0_320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${inputClass} w-full pl-9`} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search candidate, company, role, offer..." />
              </label>
              <select className={`${inputClass} w-[160px] shrink-0`} value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
                {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[205px] shrink-0`} value={filters.negotiationStatus} onChange={(event) => setFilters((current) => ({ ...current, negotiationStatus: event.target.value }))}>
                {negotiationOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[170px] shrink-0`} value={filters.signature} onChange={(event) => setFilters((current) => ({ ...current, signature: event.target.value }))}>
                {signatureOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[170px] shrink-0`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button type="submit" className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
                Search
              </button>
            </form>
          </div>

          {!offers.length ? (
            <div className="p-5">
              <EmptyState title="No offers found" copy="Adjust search or filters to review offer activity." icon={FileSignature} onReset={resetFilters} />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-[1260px] divide-y divide-slate-100">
                  <thead className="bg-slate-50/50">
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">Candidate</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Offer</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Signature</th>
                      <th className="px-6 py-4">Timeline</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {offers.map((offer) => (
                      <tr key={offer.id} className="align-top transition hover:bg-slate-50/80">
                        <td className="px-6 py-4">
                          <p className="font-semibold text-slate-900">{offer.candidate?.name || 'Candidate'}</p>
                          <p className="mt-1 text-sm text-slate-500">{offer.candidate?.email || 'No email'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800">{offer.job?.title || 'Untitled role'}</p>
                          <p className="mt-1 text-sm text-slate-500">{offer.employer?.companyName || 'Employer not set'}</p>
                          <p className="mt-1 text-xs text-slate-500">{offer.match?.score === null ? 'No score' : `${Math.round(offer.match?.score || 0)}% match`}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="max-w-[220px] truncate text-sm font-semibold text-slate-900">{offer.title || 'Offer'}</p>
                          <p className="mt-1 text-sm text-slate-600">{formatSalary(offer)}</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(offer.employmentType)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={offer.status} />
                          <p className="mt-2 text-xs text-slate-500">{formatNumber(offer.negotiationSummary?.pending)} pending negotiations</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{offer.signatureRequired ? 'Required' : 'Not required'}</p>
                          <p className="mt-1 text-xs text-slate-500">{offer.candidateSignedAt ? `Signed ${formatDate(offer.candidateSignedAt)}` : 'Not signed'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">Created {formatDate(offer.createdAt)}</p>
                          <p className="mt-1 text-xs text-slate-500">Expires {formatDate(offer.expiresAt)}</p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button type="button" onClick={() => openDetails(offer)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 xl:hidden">
                {offers.map((offer) => (
                  <article key={offer.id} className="rounded-[24px] border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate font-semibold text-slate-900">{offer.candidate?.name || 'Candidate'}</h2>
                        <p className="mt-1 text-sm text-slate-500">{offer.job?.title || 'Untitled role'}</p>
                      </div>
                      <StatusBadge status={offer.status} />
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <p className="flex items-center gap-2 text-sm text-slate-600"><Banknote className="h-4 w-4 text-slate-400" /> {formatSalary(offer)}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><MessageSquare className="h-4 w-4 text-slate-400" /> {formatNumber(offer.negotiationSummary?.pending)} pending</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><ShieldCheck className="h-4 w-4 text-slate-400" /> {offer.signatureRequired ? 'Signature required' : 'Signature optional'}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><TimerReset className="h-4 w-4 text-slate-400" /> Expires {formatDate(offer.expiresAt)}</p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button type="button" onClick={() => openDetails(offer)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  Showing {formatNumber(pagination.offset + 1)} - {formatNumber(pagination.offset + offers.length)} of {formatNumber(pagination.total)}
                </p>
                <div className="flex gap-2">
                  <button type="button" disabled={pagination.offset <= 0 || refreshing} onClick={() => loadOffers({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Previous
                  </button>
                  <button type="button" disabled={!pagination.hasMore || refreshing} onClick={() => loadOffers({ offset: pagination.offset + pagination.limit, quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {selected && (
        <DetailModalShell
          title={selected.title || 'Offer'}
          subtitle={`${selected.candidate?.name || 'Candidate'} · ${selected.job?.title || 'Role'}`}
          loading={detailLoading}
          onClose={() => {
            setSelected(null);
            setDetail(null);
          }}
        >
          <OfferDetails detail={detail} />
        </DetailModalShell>
      )}
    </div>
  );
}

export default OffersListPage;
